import { type MailAccountInput, OWN_SERVER_DEFAULTS } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Eye, EyeOff, Lock, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { api, type MailFolder } from '../../lib/api'
import { openOAuthLink } from '../../lib/oauth-link'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select } from '../ui/select'

const PRESETS: Array<{
  label: string
  imapHost: string
  imapPort: number
  smtpHost: string
  smtpPort: number
  smtpSecure: boolean
  help?: string
}> = [
  {
    label: 'Gmail',
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: false,
    help: 'Benötigt App-Passwort (myaccount.google.com → Sicherheit → App-Passwörter)',
  },
  {
    label: 'Office365',
    imapHost: 'outlook.office365.com',
    imapPort: 993,
    smtpHost: 'smtp.office365.com',
    smtpPort: 587,
    smtpSecure: false,
    help: 'IMAP muss vom Admin freigeschaltet sein; ggf. App-Passwort statt MFA-Hauptpasswort',
  },
  {
    label: 'iCloud',
    imapHost: 'imap.mail.me.com',
    imapPort: 993,
    smtpHost: 'smtp.mail.me.com',
    smtpPort: 587,
    smtpSecure: false,
    help: 'App-spezifisches Passwort unter appleid.apple.com erzeugen',
  },
  {
    label: 'Eigener Server',
    ...OWN_SERVER_DEFAULTS,
  },
]

/** Microsoft-OAuth login section shown only in the Office365 preset.
 * When the admin has configured the Azure-AD app, this is the primary
 * way to add an Office365 account (Basic Auth for IMAP is dead at
 * Microsoft). Falls back to a tip when OAuth isn't configured yet. */
function MicrosoftOAuthSection({
  label,
  fromName,
  email,
}: {
  label: string
  fromName: string
  email: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => api.getAppSettings(),
  })
  if (isLoading) return null
  const configured =
    !!data?.settings.oauthMicrosoftClientId && !!data?.settings.oauthMicrosoftSecretSet
  if (!configured) {
    return (
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-text-secondary">
        Microsoft hat Basic-Auth für IMAP abgeschaltet. Für Office365 muss ein Admin in den{' '}
        <a
          href={`${import.meta.env.BASE_URL}settings/mail-oauth`}
          className="text-accent hover:underline"
        >
          Mail-OAuth-Einstellungen
        </a>{' '}
        die Azure-AD-App eintragen, damit du dich hier mit Microsoft anmelden kannst.
      </div>
    )
  }
  return (
    <div className="rounded-md border border-accent/30 bg-accent/5 p-3 space-y-2">
      <p className="text-xs text-text-secondary">
        Empfohlen: Login direkt bei Microsoft — Tokens werden automatisch aktualisiert, kein
        Passwort nötig.
      </p>
      <Button
        type="button"
        variant="primary"
        onClick={() => {
          openOAuthLink(
            api.mailOAuthStartUrl({
              label: label.trim() || email.trim(),
              fromName: fromName.trim(),
            }),
          )
        }}
        disabled={!email.trim()}
        className="w-full"
      >
        Mit Microsoft anmelden
      </Button>
      <p className="text-[11px] text-text-tertiary">
        Tipp: Trag oben deine Mail-Adresse + Bezeichnung ein, bevor du klickst.
      </p>
    </div>
  )
}

export function AddAccountDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const qc = useQueryClient()
  const [preset, setPreset] = useState('Eigener Server')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [imapUser, setImapUser] = useState('')
  const [smtpUser, setSmtpUser] = useState('')
  const [smtpPassword, setSmtpPassword] = useState('')
  const [visiblePassword, setVisiblePassword] = useState(false)
  const [advanced, setAdvanced] = useState(false)
  const [replaceOAuth, setReplaceOAuth] = useState(true)
  const [connection, setConnection] = useState<{
    imapHost: string
    imapPort: number
    imapSecure: boolean
    smtpHost: string
    smtpPort: number
    smtpSecure: boolean
    tlsServername: string
  }>({ ...OWN_SERVER_DEFAULTS })
  const [saved, setSaved] = useState<{
    id: string
    folders: MailFolder[]
    warning?: string
  } | null>(null)
  const {
    data: accountsData,
    isError: accountsError,
    isPending: accountsPending,
  } = useQuery({ queryKey: ['mail-accounts'], queryFn: () => api.listMailAccounts() })
  const oauthAccounts =
    accountsData?.accounts.filter((a) => a.authType === 'oauth-microsoft' && !a.disabledAt) ?? []
  const input: MailAccountInput = {
    ...connection,
    label: name.trim() || email.trim(),
    fromName: name.trim() || null,
    email: email.trim(),
    imapUser: imapUser.trim() || email.trim(),
    smtpUser: smtpUser.trim() || email.trim(),
    imapPassword: password,
    smtpPassword: smtpPassword || password,
  }
  const verify = useMutation({ mutationFn: () => api.verifyMailAccount(input) })
  const create = useMutation({
    mutationFn: async () => {
      const { account } = await api.createMailAccount(input)
      // The account exists even if a subsequent sync/disable fails. Never offer another create.
      setSaved({ id: account.id, folders: [] })
      let folders: MailFolder[] = []
      const warnings: string[] = []
      try {
        folders = (await api.syncMailAccount(account.id)).folders
      } catch (error) {
        warnings.push(
          `Konto gespeichert, Ordnerprüfung fehlgeschlagen: ${(error as Error).message}`,
        )
      }
      if (preset === 'Eigener Server' && replaceOAuth && warnings.length === 0) {
        const results = await Promise.allSettled(
          oauthAccounts.map((a) => api.disableMailAccount(a.id)),
        )
        if (results.some((r) => r.status === 'rejected'))
          warnings.push(
            'Mindestens ein altes Konto konnte nicht deaktiviert werden. Bitte unter Mail-Konten erneut deaktivieren.',
          )
      }
      setSaved({ id: account.id, folders, warning: warnings.join(' ') || undefined })
      await qc.invalidateQueries({ queryKey: ['mail-accounts'] })
    },
  })
  const busy = verify.isPending || create.isPending
  const checked = verify.data?.imap.ok && verify.data?.smtp.ok
  const valid =
    !!email.trim() && !!password && !!connection.imapHost.trim() && !!connection.smtpHost.trim()
  function changeConnection(patch: Partial<typeof connection>) {
    setConnection((c) => ({ ...c, ...patch }))
    verify.reset()
  }
  return (
    <Dialog
      open
      onClose={busy ? () => {} : onClose}
      title={
        saved
          ? 'Konto gespeichert'
          : preset === 'Eigener Server'
            ? 'Eigener Server verbinden'
            : 'Mail-Konto verbinden'
      }
      description={
        saved
          ? 'Ordner wurden vom Server übernommen.'
          : 'Server ist vorbelegt, nur Konto und App-Passwort eintragen.'
      }
      className="sm:max-w-lg"
      footer={
        saved ? (
          <Button disabled={busy} onClick={() => onCreated(saved.id)}>
            Fertig
          </Button>
        ) : (
          <>
            <Button variant="secondary" type="submit" form="add-mail" disabled={!valid || busy}>
              <RefreshCw className="h-4 w-4" />
              {verify.isPending ? 'Prüfe …' : 'Verbindung prüfen'}
            </Button>
            <Button
              disabled={!checked || busy || accountsPending || accountsError}
              onClick={() => create.mutate()}
            >
              <Check className="h-4 w-4" />
              {create.isPending ? 'Speichere …' : 'Konto speichern'}
            </Button>
          </>
        )
      }
    >
      {saved ? (
        <div className="space-y-3 text-sm py-2" aria-live="polite">
          {saved.warning && (
            <p role="alert" className="text-warning">
              {saved.warning}
            </p>
          )}
          {create.isPending ? (
            <p>Ordner prüfen und bisherige Konten ablösen …</p>
          ) : (
            <>
              <p>
                Posteingang:{' '}
                {saved.folders.find((f) => f.path.toUpperCase() === 'INBOX')?.name ??
                  'Nicht gefunden'}
              </p>
              {(
                [
                  ['\\Sent', 'Gesendet'],
                  ['\\Trash', 'Papierkorb'],
                  ['\\Archive', 'Archiv'],
                  ['\\Drafts', 'Entwürfe'],
                  ['\\Junk', 'Spam'],
                ] as const
              ).map(([flag, label]) => (
                <p key={flag}>
                  {label}:{' '}
                  {saved.folders.find((f) => f.flags.includes(flag))?.name ??
                    'Keine SPECIAL-USE-Zuordnung gemeldet'}
                </p>
              ))}
            </>
          )}
        </div>
      ) : (
        <form
          id="add-mail"
          className="space-y-3 py-1"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid && !busy) verify.mutate()
          }}
        >
          <fieldset disabled={busy} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="m-name">Anzeigename</Label>
              <Input
                id="m-name"
                value={name}
                placeholder="Max Muster"
                onChange={(e) => {
                  setName(e.target.value)
                  verify.reset()
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="m-email">E-Mail-Adresse</Label>
              <Input
                id="m-email"
                type="email"
                required
                value={email}
                placeholder="max@beispiel.de"
                autoComplete="username"
                onChange={(e) => {
                  setEmail(e.target.value)
                  verify.reset()
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="m-host">Host</Label>
              <div className="relative">
                <Input
                  id="m-host"
                  readOnly
                  value={connection.imapHost}
                  className="pr-10 font-mono text-text-secondary"
                />
                <Lock className="absolute right-3 top-3 h-4 w-4 text-text-tertiary" />
              </div>
              <p className="text-xs text-text-tertiary">
                IMAP {connection.imapPort} · SMTP {connection.smtpPort}
                {connection.imapSecure && connection.smtpSecure ? ', TLS.' : '.'} Host und Ports
                unter „Erweitert“ ändern.
              </p>
            </div>
            <details open={advanced} onToggle={(e) => setAdvanced(e.currentTarget.open)}>
              <summary className="cursor-pointer text-xs text-text-secondary py-1">
                Erweitert
              </summary>
              <div className="space-y-3 pt-2">
                <Label htmlFor="m-preset">Vorlage</Label>
                <Select
                  id="m-preset"
                  value={preset}
                  onChange={(e) => {
                    const selected = PRESETS.find((p) => p.label === e.target.value)
                    if (!selected) return
                    setPreset(selected.label)
                    setConnection({
                      imapHost: selected.imapHost,
                      imapPort: selected.imapPort,
                      imapSecure: true,
                      smtpHost: selected.smtpHost,
                      smtpPort: selected.smtpPort,
                      smtpSecure: selected.smtpSecure,
                      tlsServername:
                        selected.label === 'Eigener Server'
                          ? OWN_SERVER_DEFAULTS.tlsServername
                          : '',
                    })
                    verify.reset()
                  }}
                >
                  {PRESETS.map((p) => (
                    <option key={p.label}>{p.label}</option>
                  ))}
                </Select>
                {preset === 'Office365' && (
                  <MicrosoftOAuthSection label={name} fromName={name} email={email} />
                )}
                {(['imap', 'smtp'] as const).map((protocol) => (
                  <fieldset
                    key={protocol}
                    className="space-y-2 border border-border-subtle rounded-lg p-3"
                  >
                    <legend className="text-xs uppercase">{protocol}</legend>
                    <Label htmlFor={`m-${protocol}-host`}>Server</Label>
                    <Input
                      id={`m-${protocol}-host`}
                      value={connection[`${protocol}Host`]}
                      onChange={(e) => changeConnection({ [`${protocol}Host`]: e.target.value })}
                    />
                    <Label htmlFor={`m-${protocol}-port`}>Port</Label>
                    <Input
                      id={`m-${protocol}-port`}
                      type="number"
                      min={1}
                      max={65535}
                      value={connection[`${protocol}Port`]}
                      onChange={(e) =>
                        changeConnection({ [`${protocol}Port`]: Number(e.target.value) })
                      }
                    />
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        id={`m-${protocol}-secure`}
                        type="checkbox"
                        checked={connection[`${protocol}Secure`]}
                        onChange={(e) =>
                          changeConnection({ [`${protocol}Secure`]: e.target.checked })
                        }
                      />
                      TLS ab Verbindungsaufbau
                    </label>
                  </fieldset>
                ))}
                <Label htmlFor="m-imap-user">IMAP-Benutzer (optional)</Label>
                <Input
                  id="m-imap-user"
                  value={imapUser}
                  placeholder={email || 'E-Mail-Adresse'}
                  onChange={(e) => {
                    setImapUser(e.target.value)
                    verify.reset()
                  }}
                />
                <Label htmlFor="m-smtp-user">SMTP-Benutzer (optional)</Label>
                <Input
                  id="m-smtp-user"
                  value={smtpUser}
                  placeholder={email || 'E-Mail-Adresse'}
                  onChange={(e) => {
                    setSmtpUser(e.target.value)
                    verify.reset()
                  }}
                />
                <Label htmlFor="m-smtp-password">Abweichendes SMTP-App-Passwort (optional)</Label>
                <Input
                  id="m-smtp-password"
                  type="password"
                  autoComplete="new-password"
                  value={smtpPassword}
                  placeholder="Gleiches App-Passwort wie IMAP"
                  onChange={(e) => {
                    setSmtpPassword(e.target.value)
                    verify.reset()
                  }}
                />
                <Label htmlFor="m-servername">TLS-Servername (Zertifikat)</Label>
                <Input
                  id="m-servername"
                  value={connection.tlsServername}
                  placeholder={OWN_SERVER_DEFAULTS.tlsServername}
                  onChange={(e) => changeConnection({ tlsServername: e.target.value })}
                />
              </div>
            </details>
            <div className="space-y-1">
              <Label htmlFor="m-password">App-Passwort</Label>
              <div className="relative">
                <Input
                  id="m-password"
                  required
                  type={visiblePassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="pr-12"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    verify.reset()
                  }}
                />
                <button
                  type="button"
                  aria-label={visiblePassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  className="absolute right-0 top-0 p-3 text-text-tertiary"
                  onClick={() => setVisiblePassword((v) => !v)}
                >
                  {visiblePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-text-tertiary">
                {preset === 'Eigener Server'
                  ? 'App-Passwort aus Stalwart, kein Kontopasswort.'
                  : PRESETS.find((p) => p.label === preset)?.help}{' '}
                Wird AES-256-verschlüsselt gespeichert.
              </p>
            </div>
            {preset === 'Eigener Server' && oauthAccounts.length > 0 && (
              <label className="flex items-start gap-2 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={replaceOAuth}
                  onChange={(e) => setReplaceOAuth(e.target.checked)}
                />
                Bisherige Microsoft-OAuth-Konten deaktivieren (
                {oauthAccounts.map((a) => a.label).join(', ')}). Gespeicherte Mails bleiben lesbar.
              </label>
            )}
          </fieldset>
          {verify.data && (
            <div
              role="status"
              className={`rounded-xl border p-3 text-xs ${checked ? 'border-success/25 bg-success/10 text-success' : 'border-danger/25 bg-danger/10 text-danger'}`}
            >
              <p className="font-semibold text-sm">
                {checked
                  ? `IMAP + SMTP erreichbar${connection.imapSecure && connection.smtpSecure ? ', TLS gültig' : ''}`
                  : 'Verbindung fehlgeschlagen'}
              </p>
              {checked ? (
                <p className="text-text-secondary mt-1">
                  Anmeldung geprüft ·{' '}
                  {(verify.data.durationMs / 1000).toLocaleString('de-DE', {
                    maximumFractionDigits: 1,
                  })}{' '}
                  s
                  {!connection.imapSecure || !connection.smtpSecure
                    ? ' · Keine durchgehende TLS-Verbindung bestätigt'
                    : ''}
                </p>
              ) : (
                <p>
                  {verify.data.imap.error && `IMAP: ${verify.data.imap.error} `}
                  {verify.data.smtp.error && `SMTP: ${verify.data.smtp.error}`}
                </p>
              )}
            </div>
          )}
          {(verify.error || create.error || accountsError) && (
            <p role="alert" className="text-xs text-danger">
              {verify.error?.message ||
                create.error?.message ||
                'Bestehende Konten konnten nicht geladen werden.'}
            </p>
          )}
        </form>
      )}
    </Dialog>
  )
}
